import Button from "@/components/Button/button";
import classnames from "classnames";
import SbEditable from "storyblok-react";
import { usePricingExplorer } from "./PricingExplorerContext";
import styles from "./pricing-explorer.module.scss";
import type { PricingExplorerVariant, PricingPlanStoryblok } from "./types";

interface Props {
  blok: PricingPlanStoryblok;
  index: number;
  variant: PricingExplorerVariant;
}

export const PlanCard = ({ blok, index, variant }: Props) => {
  const { billingPeriod, selectedPlanIndex, setHoveredPlanIndex } =
    usePricingExplorer();
  const featureItems = (blok.features || []).filter((feature) =>
    feature?.text?.trim()
  );
  const hasFeatureList =
    variant === "detailed" &&
    Boolean(blok.features_prefix || featureItems.length);

  const price =
    billingPeriod === "monthly" ? blok.price_monthly : blok.price_yearly;
  const ctaLink =
    billingPeriod === "monthly" ? blok.cta_link_monthly : blok.cta_link_yearly;

  const classes = classnames(styles.planCard, {
    [styles.planCardSelected]: selectedPlanIndex === index,
    [styles.planCardHighlight]: blok.highlight,
    [styles[`planCardTheme_${blok.theme || "light"}`]]: true,
  });

  return (
    // @ts-expect-error
    <SbEditable content={blok}>
      <div
        className={classes}
        onMouseEnter={() => setHoveredPlanIndex(index)}
        onMouseLeave={() => setHoveredPlanIndex(null)}
        onFocus={() => setHoveredPlanIndex(index)}
        onBlur={() => setHoveredPlanIndex(null)}
      >
        <div className={styles.planCardContent}>
          <div className={styles.planCardHeader}>
            <h3 className={styles.planCardName}>{blok.name}</h3>
            {blok.badge ? (
              <span className={styles.planCardBadge}>{blok.badge}</span>
            ) : null}
          </div>
          {blok.subtitle ? (
            <p className={styles.planCardSubtitle}>{blok.subtitle}</p>
          ) : null}
          {hasFeatureList ? (
            <ul className={styles.planCardFeatures}>
              {blok.features_prefix ? (
                <li className={styles.planCardFeaturesPrefix}>
                  {blok.features_prefix}
                </li>
              ) : null}
              {featureItems.map((feature) => (
                <li key={feature._uid} className={styles.planCardFeature}>
                  {feature.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className={styles.planCardFooter}>
          <div className={styles.planCardPricing}>
            <span className={styles.planCardPrice}>{price}</span>
            <span className={styles.planCardPer}>/month</span>
          </div>
          {ctaLink ? (
            <Button
              link={ctaLink}
              btnStyle="border"
              size="medium"
              className={styles.planCardCtaButton}
            >
              {blok.cta_text || "Sign up"}
            </Button>
          ) : null}
        </div>
      </div>
    </SbEditable>
  );
};
